/**
 * Tabbed Featured Collection
 * Custom element for tab switching with keyboard navigation and data-step updates
 */

(function() {
  if (customElements.get('tabbed-featured-collection')) return;

  // Mirrors getFocusableElements() in global.js; only the first match is needed here.
  var FOCUSABLE_SELECTOR = "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe";

  class TabbedFeaturedCollection extends HTMLElement {
  constructor() {
    super();
    this._onTabClick = this._onTabClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onMediaChange = this._updateArrowSteps.bind(this);
    this._onBlockSelect = this._onBlockSelect.bind(this);
    this._onSectionUnload = this._onSectionUnload.bind(this);
  }

  connectedCallback() {
    this._tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this._panels = Array.from(this.querySelectorAll('.tfc__panel'));

    // Tab event listeners (only if multiple tabs)
    if (this._tabs.length >= 2) {
      this._tabs.forEach(function(tab) {
        tab.addEventListener('click', this._onTabClick);
        tab.addEventListener('keydown', this._onKeyDown);
      }.bind(this));
    }

    // Media query listener (always needed for arrow steps)
    this._mediaQuery = window.matchMedia('(min-width: 750px)');
    this._mediaQuery.addEventListener('change', this._onMediaChange);

    this._updateArrowSteps();
    this._loadActiveDynamicSource();
    this._updateSectionVisibility();

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('shopify:section:unload', this._onSectionUnload);
    }
  }

  disconnectedCallback() {
    this.dispatchEvent(new CustomEvent('tabbed-featured-collection:disconnect'));

    this._tabs.forEach(function(tab) {
      tab.removeEventListener('click', this._onTabClick);
      tab.removeEventListener('keydown', this._onKeyDown);
    }.bind(this));

    if (this._mediaQuery) {
      this._mediaQuery.removeEventListener('change', this._onMediaChange);
    }

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.removeEventListener('shopify:section:unload', this._onSectionUnload);
    }
  }

  _onTabClick(event) {
    this._activateTab(event.currentTarget.dataset.tabIndex);
  }

  _onKeyDown(event) {
    var currentIndex = this._tabs.indexOf(event.currentTarget);
    var newIndex;

    switch (event.key) {
      case 'ArrowLeft':
        newIndex = currentIndex > 0 ? currentIndex - 1 : this._tabs.length - 1;
        break;
      case 'ArrowRight':
        newIndex = currentIndex < this._tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = this._tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    var targetTab = this._tabs[newIndex];
    this._activateTab(targetTab.dataset.tabIndex);
    targetTab.focus();
  }

  /**
   * Static, non-active panels ship their products inside <template data-tfc-lazy>
   * so they stay out of the DOM until the tab is opened.
   */
  _hydratePanel(panel) {
    var lazy = panel.querySelector('template[data-tfc-lazy]');
    if (!lazy) return;
    lazy.replaceWith(lazy.content);

    // Same post-insert hooks the dynamic sources run (see refreshInjectedSlider):
    // desktop hover images, stickers and custom selects for the new cards.
    if (window.renderSelects) window.renderSelects();
    if (window.loadDesktopOnlyTemplates) window.loadDesktopOnlyTemplates();
    if (window.stickersReinit) window.stickersReinit();
  }

  _activateTab(index) {
    this._tabs.forEach(function(tab) {
      var isActive = tab.dataset.tabIndex === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.classList.toggle('is-active', isActive);
    });

    var component = this;
    this._panels.forEach(function(panel) {
      var isActive = panel.dataset.tabIndex === index;
      panel.hidden = !isActive;

      if (isActive) {
        component._hydratePanel(panel);
        var slider = panel.querySelector('slider-component');
        if (slider) {
          var sliderEl = slider.querySelector('.slider');
          if (sliderEl) sliderEl.scrollLeft = 0;

          requestAnimationFrame(function() {
            if (slider.resetPages) {
              slider.resetPages();
            } else {
              window.dispatchEvent(new Event('resize'));
            }
          });
        }

        if (panel.dataset.sourceType && panel.dataset.sourceState === 'idle' && window.loadTabbedFeaturedCollectionSource) {
          window.loadTabbedFeaturedCollectionSource(panel, this);
        }
      }
    });

    this._updateArrowSteps();
  }

  _loadActiveDynamicSource() {
    var activePanel = this._panels.find(function(panel) {
      return !panel.hidden;
    });

    if (activePanel && activePanel.dataset.sourceType && activePanel.dataset.sourceState === 'idle' && window.loadTabbedFeaturedCollectionSource) {
      window.loadTabbedFeaturedCollectionSource(activePanel, this);
    }
  }

  /**
   * Hides the whole section container (header and spacing included) when
   * hide_when_empty is enabled and every panel is a terminal empty source.
   * Static/loaded content or a still-idle/loading dynamic source keeps it visible.
   */
  _updateSectionVisibility() {
    var container = this.closest('[data-tfc-section-container]');
    if (!container) return;

    var hideWhenEmpty = this.dataset.hideWhenEmpty === 'true';
    var designMode = typeof Shopify !== 'undefined' && Shopify.designMode;
    if (!hideWhenEmpty || designMode) {
      container.hidden = false;
      return;
    }

    var panels = this._panels || [];
    var hasVisibleContent = panels.some(function(panel) {
      var state = panel.dataset.sourceState;
      if (panel.dataset.tfcHasProducts === 'true' || state === 'loaded') return true;
      return state === 'idle' || state === 'loading';
    });

    if (hasVisibleContent) {
      container.hidden = false;
      return;
    }

    this._moveFocusBeforeHide(container);
    container.hidden = true;
  }

  /**
   * Keyboard focus must never remain inside content that is about to be hidden.
   * Move it to the first focusable control in the next section region, or blur.
   */
  _moveFocusBeforeHide(container) {
    var active = document.activeElement;
    if (!active || !container.contains(active)) return;

    var section = container.closest('.shopify-section') || container;
    var region = section.nextElementSibling;
    var next = region && region.querySelector ? region.querySelector(FOCUSABLE_SELECTOR) : null;

    if (next && typeof next.focus === 'function') {
      next.focus();
    } else if (typeof active.blur === 'function') {
      active.blur();
    }
  }

  _updateArrowSteps() {
    var transition = this.dataset.arrowTransition;
    if (transition !== 'one_page') {
      this.querySelectorAll('.slider-button').forEach(function(btn) {
        btn.dataset.step = 1;
      });
      return;
    }

    var isDesktop = this._mediaQuery ? this._mediaQuery.matches : window.innerWidth >= 750;
    var step = isDesktop ? this.dataset.stepDesktop : this.dataset.stepMobile;

    this.querySelectorAll('.slider-button').forEach(function(btn) {
      btn.dataset.step = step;
    });
  }

  _onBlockSelect(event) {
    if (!this.contains(event.target)) return;
    var block = event.target;
    var index = block.dataset.tabIndex;
    if (index) this._activateTab(index);
  }

  _onSectionUnload(event) {
    var sectionEl = this.closest('shopify-section');
    if (event.target !== sectionEl) return;
    this.disconnectedCallback();
  }
}

  customElements.define('tabbed-featured-collection', TabbedFeaturedCollection);
})();
